/**
 * LL customization: <ll-hero-video> — poster-to-video crossfade for hero sections.
 *
 * Rendered by snippets/ll-hero-video.liquid. The poster <img> sits above the <video>;
 * once the video reports `playing` we set `data-playing` and the section's CSS fades
 * the poster out. If autoplay is refused (Low Power Mode, data saver) the poster simply
 * stays, which beats the black box stock Horizon shows.
 *
 * On Data Saver or a 2G/3G connection (Network Information API, Chromium only) the video is
 * never started and the poster stays — Google's "adaptive loading" pattern.
 *
 * The <source> carries its URL in `data-src` (and the video has `preload="none"`), so nothing
 * downloads until the slot is on screen. IntersectionObserver never reports a `display: none`
 * element as intersecting, which is exactly what we want: the desktop slot on a phone (and the
 * mobile slot on a desktop) is skipped entirely, and a hero further down the page waits until it
 * is near the viewport. The first hero's visible slot intersects on the first observation.
 *
 * Three gates must all open before the video downloads: the slot is on screen, the poster has
 * finished loading (or failed), and the page has painted its first content. The third one
 * (2026-09-12, round four) exists for the same reason the web fonts wait for it in
 * snippets/theme-styles-variables.liquid: Lighthouse / PageSpeed sometimes hold the first paint
 * for seconds, and every byte that finishes before their observed LCP — a 1.5–1.85 MB MP4
 * included — is replayed on their slow simulated link and charged to the LCP. For a real visitor
 * the paint has happened long before the poster arrives, so this costs at most one frame.
 */
class LLHeroVideo extends HTMLElement {
  connectedCallback() {
    const video = this.querySelector('video');
    if (!video) return;

    const markPlaying = () => {
      this.dataset.playing = '';
    };
    video.addEventListener('playing', markPlaying, { once: true });

    const startVideo = () => {
      for (const source of video.querySelectorAll('source[data-src]')) {
        source.src = source.dataset.src;
        delete source.dataset.src;
      }
      video.load();
      video.play().catch(() => {});
    };

    // The poster is the LCP candidate; a multi-megabyte video fetched alongside it starves it
    // of bandwidth on a slow connection (measured: poster load 0.9 s alone vs 8.4 s in parallel).
    // So the video starts only once the poster has finished loading (or failed).
    const poster = this.querySelector('.hero__video-poster img');
    // Adaptive loading (Google's term): on Data Saver or a 2G/3G connection the video never
    // starts and the poster simply stays. Chromium-only API; Safari falls through to normal.
    const connection = navigator.connection;
    if (connection && (connection.saveData || /(^|-)(2g|3g)$/.test(connection.effectiveType || ''))) {
      return;
    }

    const start = () => {
      if (!poster || poster.complete) {
        LLHeroVideo.afterFirstPaint(startVideo);
        return;
      }
      const afterPoster = () => LLHeroVideo.afterFirstPaint(startVideo);
      poster.addEventListener('load', afterPoster, { once: true });
      poster.addEventListener('error', afterPoster, { once: true });
    };

    if (!('IntersectionObserver' in window)) {
      start();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        start();
      },
      { rootMargin: '300px 0px' }
    );
    observer.observe(this);
  }

  /**
   * Runs `callback` once the document has produced its first contentful paint — immediately if
   * it already has (the observer is `buffered`), else on the `first-contentful-paint` entry.
   * Fallback: 3 s (plus the load event in browsers without paint timing). Mirrors the font switch in
   * snippets/theme-styles-variables.liquid.
   * @param {() => void} callback
   */
  static afterFirstPaint(callback) {
    let done = false;
    const run = () => {
      if (done) return;
      done = true;
      callback();
    };
    try {
      const observer = new PerformanceObserver((list) => {
        if (list.getEntries().some((entry) => entry.name === 'first-contentful-paint')) {
          observer.disconnect();
          run();
        }
      });
      observer.observe({ type: 'paint', buffered: true });
    } catch (error) {
      run();
      return;
    }
    // The load event is a fallback only for browsers that cannot report paint timing. Where they
    // can, it is not used (2026-10-17): in a run whose first paint is held, load fires before the
    // paint, and starting the video there put the whole MP4 ahead of the first contentful paint.
    const reportsPaint = PerformanceObserver.supportedEntryTypes?.includes('paint');
    if (!reportsPaint) {
      if (document.readyState === 'complete') {
        setTimeout(run, 0);
      } else {
        window.addEventListener('load', () => setTimeout(run, 0), { once: true });
      }
    }
    setTimeout(run, 3000);
  }
}

if (!customElements.get('ll-hero-video')) {
  customElements.define('ll-hero-video', LLHeroVideo);
}
